<template>
  <b-card>
    <b-row
      v-if="isLoading"
      class="d-flex align-content-center justify-content-center my-2"
    >
      <b-spinner
        type="grow"
        label="Loading..."
      />
    </b-row>

    <b-row v-else>
      <b-col
        cols="12"
        class="d-flex align-items-center justify-content-end mb-1"
      >
        <b-button
          variant="primary"
          @click="$router.push({ name: 'start-new-personnel-evaluation' })"
        >
          <span class="text-nowrap">Start New Evaluation</span>
        </b-button>
      </b-col>

      <b-col cols="12">
        <FilterEvaluation
          :active-user.sync="selectActiveUser"
          :main-cols="allMainCols"
          :certificate-value="selectCertificateCategory"
          :user-value="selectUserId"
          :product-value="selectProductId"
          @change-select="handleChangeSelect"
          @reset-all="resetAllFilters"
          @reload-server-data="forceReloadServerData"
        />
      </b-col>

      <!-- Client-side filters: Source, Evaluation Final, Branch -->
      <b-col cols="12">
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group label="Source">
              <v-select
                :options="sourceOptions"
                :value="selectSource"
                placeholder="All Sources"
                @input="selectSource = $event"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group label="Evaluation Final">
              <v-select
                :options="evaluationFinalOptions"
                :value="selectEvaluationFinal"
                placeholder="All Levels"
                @input="selectEvaluationFinal = $event"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <b-form-group label="Branch">
              <v-select
                :options="branchOptions"
                :value="selectBranch"
                placeholder="All Branches"
                @input="selectBranch = $event"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-center"
          >
            <b-form-checkbox v-model="selectLateOnly">
              Late Only
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-col>

      <PersonnelTable
        :main-cols="mainCols"
        :grouped-users="filteredGroupedUsers"
        :header-data="headerData"
      />
    </b-row>
  </b-card>
</template>

<script>
import vSelect from 'vue-select'
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import FilterEvaluation from '../personnel-action/FilterEvaluation.vue'
import PersonnelTable from '../personnel-components/PersonnelTable.vue'
import { levelIds, levelLabel, describeChange } from '../personnel-components/ceLevels'

export default {
  name: 'PersonnelEvaluationMasterList',
  components: {
    FilterEvaluation,
    PersonnelTable,
    vSelect,
  },
  data() {
    return {
      isLoading: false,
      baseHeaderData: [
        'Certificate Category',
        'Eng Name',
        'Evaluation Final',
        'Evaluation Date',
        'Next Evaluation',
        'Evaluator',
        'Branch',
        'Sources',
      ],
      mainCols: [],
      allMainCols: [],
      groupedUsers: {},
      rawData: [],

      selectCertificateCategory: null,
      selectUserId: null,
      selectProductId: null,
      selectActiveUser: true,

      selectSource: null,
      selectBranch: null,
      selectEvaluationFinal: null,
      selectLateOnly: false,
    }
  },

  computed: {
    /**
     * 'internal' or 'external', taken from the route so one component serves both sections.
     * Undefined on any route that does not set it, which keeps the old "show everything"
     * behaviour for anything still pointing here.
     */
    employment() {
      return this.$route.meta?.employment || null
    },
    isExternalList() {
      return this.employment === 'external'
    },
    // "Active" (GIF-HR-020/026 checklist status) only makes sense for external inspectors.
    headerData() {
      return this.isExternalList ? [...this.baseHeaderData, 'Active'] : this.baseHeaderData
    },
    sourceOptions() {
      const all = this.rawData.flatMap(u => {
        if (!u.source) return []
        return Array.isArray(u.source) ? u.source : [u.source]
      })
      return [...new Set(all)].filter(Boolean)
    },
    branchOptions() {
      const names = this.rawData
        .map(u => (u.branch && typeof u.branch === 'object' ? u.branch.name : u.branch))
        .filter(Boolean)
      return [...new Set(names)]
    },
    evaluationFinalOptions() {
      return [...new Set(this.rawData.map(u => u.evaluationFinal).filter(Boolean))]
    },
    filteredGroupedUsers() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const result = {}
      Object.entries(this.groupedUsers).forEach(([certId, users]) => {
        const filtered = users.filter(user => {
          if (this.selectSource) {
            let srcs = []
            if (Array.isArray(user.source)) srcs = user.source
            else if (user.source) srcs = [user.source]
            if (!srcs.includes(this.selectSource)) return false
          }
          if (this.selectBranch) {
            const branchName = typeof user.branch === 'object' ? user.branch?.name : user.branch
            if (branchName !== this.selectBranch) return false
          }
          if (this.selectEvaluationFinal && user.evaluationFinal !== this.selectEvaluationFinal) return false
          if (this.selectLateOnly) {
            if (!user.nextEvaluationDate) return false
            const d = new Date(user.nextEvaluationDate)
            d.setHours(0, 0, 0, 0)
            if (d >= today) return false
          }
          return true
        })
        if (filtered.length) result[certId] = filtered
      })
      return result
    },
  },

  watch: {
    // The internal and external lists are the same component on two routes. Vue Router reuses
    // the instance rather than rebuilding it, so mounted() does not fire on the second visit —
    // without this the page title changed but the rows stayed from the previous list.
    $route() {
      this.mainCols = []
      this.allMainCols = []
      this.groupedUsers = {}
      this.rawData = []
      this.fetchAllEvaluations()
    },
  },

  mounted() {
    this.loadQueryParams()
    if (this.selectCertificateCategory || this.selectUserId || this.selectProductId) {
      this.fetchAllCertificates()
    }
    this.fetchAllEvaluations()
  },

  methods: {
    loadQueryParams() {
      const q = this.$route.query

      if (q.certificate) this.selectCertificateCategory = Number(q.certificate)
      if (q.user) this.selectUserId = Number(q.user)
      if (q.product) this.selectProductId = q.product
      if (q.active) this.selectActiveUser = q.active === 'true'
    },
    resetAllFilters() {
      this.selectCertificateCategory = null
      this.selectUserId = null
      this.selectProductId = null
      this.selectActiveUser = true
      this.selectSource = null
      this.selectBranch = null
      this.selectEvaluationFinal = null
      this.selectLateOnly = false

      // Namespaced per list, so clear the one this route is showing as well as the
      // pre-namespacing key left behind on existing browsers.
      localStorage.removeItem(`personnel_evaluations_v4_cache_${this.employment || 'all'}`)
      localStorage.removeItem('personnel_evaluations_v4_cache')
      localStorage.removeItem('personnel_evaluations_v3_cache')
      localStorage.removeItem('personnel_evaluations_master_cache')
      this.fetchAllEvaluations()
    },
    forceReloadServerData() {
      // Namespaced per list, so clear the one this route is showing as well as the
      // pre-namespacing key left behind on existing browsers.
      localStorage.removeItem(`personnel_evaluations_v4_cache_${this.employment || 'all'}`)
      localStorage.removeItem('personnel_evaluations_v4_cache')
      localStorage.removeItem('personnel_evaluations_v3_cache')
      localStorage.removeItem('personnel_evaluations_master_cache')
      localStorage.removeItem('personnel_evaluations_master_cache_v2')
      this.fetchAllEvaluations()
    },
    handleChangeSelect(value, type) {
      if (type === 'user') this.selectUserId = value?.value
      else if (type === 'product') {
        this.selectProductId = Array.isArray(value) && value.length
          ? value.map(v => v.value).join(',')
          : null
      } else if (type === 'active-user') this.selectActiveUser = value
      else this.selectCertificateCategory = value

      this.fetchAllEvaluations()
    },
    async fetchAllCertificates() {
      try {
        const res = await axios.get('personnel-evaluation', {
          params: {
            master_data: true,
            limit: 50000,
            active_users_only: true,
            ...(this.employment ? { employment: this.employment } : {}),
          },
        })
        const personnelData = decryptData(res.data.payload).data.data
        const seen = new Set()
        personnelData.forEach(item => {
          const cert = { id: item.certificate_category.id, name: item.certificate_category.name }
          if (!seen.has(cert.id)) {
            seen.add(cert.id)
            if (!this.allMainCols.some(c => c.id === cert.id)) {
              this.allMainCols.push(cert)
            }
          }
        })
      } catch (e) { /* silent */ }
    },
    fetchAllEvaluations() {
      // The internal and external lists share this component, so the cache key has to name
      // which one it holds — otherwise opening External serves the staff rows cached by the
      // main list.
      const CACHE_KEY = `personnel_evaluations_v4_cache_${this.employment || 'all'}`
      const CACHE_TTL = 3600000 // 1 hour

      localStorage.removeItem('personnel_evaluations_master_cache')
      localStorage.removeItem('personnel_evaluations_master_cache_v2')
      localStorage.removeItem('personnel_evaluations_v3_cache')

      const cached = localStorage.getItem(CACHE_KEY)
      if (cached && !this.selectCertificateCategory && !this.selectUserId && !this.selectProductId) {
        try {
          const parsed = JSON.parse(cached)
          const isFresh = (Date.now() - parsed.timestamp) < CACHE_TTL
          if (isFresh && Array.isArray(parsed.personnelData) && parsed.personnelData.length) {
            this.processPersonnelData(parsed.personnelData)
            this.isLoading = false
            return
          }
        } catch (e) {
          localStorage.removeItem(CACHE_KEY)
        }
      }

      this.isLoading = true

      const params = {
        master_data: true,
        limit: 50000,
      }

      if (this.employment) params.employment = this.employment
      if (this.selectCertificateCategory) params.certificate_category_id = this.selectCertificateCategory
      if (this.selectUserId) params.user_id = this.selectUserId
      if (this.selectProductId) params.product = this.selectProductId
      if (this.selectActiveUser) params.active_users_only = this.selectActiveUser

      axios.get('personnel-evaluation', { params })
        .then(res => {
          const personnelData = decryptData(res.data.payload).data.data
          if (!this.selectCertificateCategory && !this.selectUserId && !this.selectProductId) {
            try {
              localStorage.setItem(CACHE_KEY, JSON.stringify({
                timestamp: Date.now(),
                personnelData,
              }))
            } catch (e) { /* silent storage quota exceeded */ }
          }
          this.processPersonnelData(personnelData)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    processPersonnelData(personnelData) {
      const seen = new Set()
      const foundCertificates = personnelData
        .map(item => ({
          id: item.certificate_category.id,
          name: item.certificate_category.name,
        }))
        .filter(item => {
          if (seen.has(item.id)) return false
          seen.add(item.id)
          return true
        })

      foundCertificates.forEach(cert => {
        if (!this.allMainCols.some(c => c.id === cert.id)) {
          this.allMainCols.push(cert)
        }
      })

      this.mainCols = this.allMainCols
      const grouped = {}
      const raw = []

      personnelData.forEach(item => {
        // Full fallback chain for engineer name so no user is ever skipped or left blank
        let engName = item.user?.name
        if (!engName || !engName.trim()) {
          if (item.user?.full_name_from_portal && item.user.full_name_from_portal.trim()) {
            engName = item.user.full_name_from_portal
          } else if (item.user?.email && item.user.email.includes('@')) {
            const rawPrefix = item.user.email.split('@')[0]
            engName = rawPrefix.charAt(0).toUpperCase() + rawPrefix.slice(1)
          } else if (item.user_name) {
            engName = item.user_name
          } else if (item.engineer_name) {
            engName = item.engineer_name
          } else if (item.engineer?.name) {
            engName = item.engineer.name
          } else {
            const userId = item.user?.id || item.user_id
            engName = userId ? `Engineer #${userId}` : 'Engineer'
          }
        }
        if (item.user && (item.user.active === 0 || item.user.active === false)) {
          engName += ' (Inactive)'
        }

        let evaluatorVal = item.evaluator
        if (evaluatorVal && typeof evaluatorVal === 'object' && evaluatorVal.name) {
          if (evaluatorVal.active === 0 || evaluatorVal.active === false) {
            evaluatorVal = { ...evaluatorVal, name: `${evaluatorVal.name} (Inactive)` }
          }
        }

        const id = item.certificate_category_id
        if (!grouped[id]) grouped[id] = []

        // Current levels = the latest evaluation's full "after" set (Caspio model, see ceLevels.js).
        const grantedLevels = levelIds(item.ce_levels_after, item.ce_level_after)
          .map(lvlId => levelLabel(lvlId, item.ce_level_after?.status))

        // Every separately completed evaluation for this person + category, newest first, each
        // with what it changed - the master row above only represents the most recent one.
        const history = Array.isArray(item.evaluation_history) ? item.evaluation_history : []
        const evaluationHistory = history.map((h, i) => {
          const before = levelIds(h.ce_levels_before)
          const after = levelIds(h.ce_levels_after, h.ce_level_after)
          return {
            date: h.completed_at,
            levels: after.map(lvlId => levelLabel(lvlId)),
            change: describeChange(before, after, i === history.length - 1),
            conclusion: h.conclusion || '',
            evaluator: h.evaluator?.name || '',
          }
        })

        let evalFinalStr = 'N/A'
        if (item.ce_level_after) {
          if (item.ce_level_after.id > 7) {
            evalFinalStr = item.ce_level_after.status
          } else {
            evalFinalStr = `${item.ce_level_after.id} ${item.ce_level_after.status}`
          }
        }

        const row = {
          engName,
          userId: item.user?.id || item.user_id,
          userActive: item.user?.active,
          evaluationFinal: evalFinalStr,
          source: item.certificate_category.schema_owner,
          branch: item.user?.office,
          lastEvaluationDate: item.completed_at,
          nextEvaluationDate: item.next_evaluation_date,
          evaluators: evaluatorVal,
          certificateCategoryId: id,
          certificateCategoryName: item.certificate_category?.name,
          allGrantedLevels: grantedLevels,
          evaluationHistory,
          isActive: item.is_active === true,
        }

        grouped[id].push(row)
        raw.push(row)
      })

      this.rawData = raw
      this.groupedUsers = grouped
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
